/**
 * Baxter composer attachments (AB#669 follow-up, AB#949 files): the attachment
 * rules plus the chip row, kept out of BaxterPanel — the rules are testable
 * without a render.
 *
 * Contract with the backend: upload to the `baxter` folder, then send
 * `context.uploaded_media: [{ id, filename, url, mime_type }]` on that ONE turn.
 */
import { useEffect, useMemo } from 'react';
import { FileAudio, FileText, FileVideo, X } from 'lucide-react';
import type { AiModel } from '@wordpuppi/shared';

/** The free router row — it picks a model at answer time, so we can't promise vision. */
export const AUTO_ID = 'openrouter/free';
export const MAX_ATTACHMENTS = 4;
/** Same as the server's media upload cap (`media.rs` MAX_FILE_SIZE). */
export const MAX_BYTES = 10 * 1024 * 1024;
/** Media folder for files pasted/attached in the chat. */
export const BAXTER_FOLDER = 'baxter';

/** Pictures Baxter looks at — `ai.rs` IMAGE_MIMES (SVG left out there too). */
const IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
/**
 * The rest of what the media upload allowlist (`media.rs` ALLOWED_MIME_TYPES)
 * takes that Baxter can use. No `audio/webm`: the server refuses it.
 */
const FILE_MIMES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'video/mp4',
  'video/webm',
];
/**
 * Browsers often leave `type` empty for .md; the server fills documents in by
 * extension (`resolve_document_mime`), so the composer does the same. Both
 * normalize Safari's .wav MIME too.
 */
const DOC_EXT_MIME: Record<string, string> = {
  md: 'text/markdown',
  txt: 'text/plain',
  pdf: 'application/pdf',
};
/** `<input accept>` for the paperclip — explicit extensions so the desktop filter matches. */
export const ATTACH_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,.pdf,.txt,.md,.mp3,.wav,.ogg,.mp4,.webm';

/** The type the server will see for this file. */
export function attachmentMime(file: { name: string; type: string }): string {
  if (file.type === 'audio/x-wav' && file.name.toLowerCase().endsWith('.wav')) return 'audio/wav';
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
  return DOC_EXT_MIME[ext] ?? file.type;
}

export const isImage = (file: { name: string; type: string }) =>
  IMAGE_MIMES.includes(attachmentMime(file));

/** "12 KB" / "3.4 MB" for the file chips. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** What the composer says when the picked model has no eyes. */
export const NO_VISION_NOTE = "This model can't see pictures — pick one with the eye icon.";

/**
 * Can the picked model be sent pictures? `value` is the picker's
 * `provider:model`. Auto is NOT able: whichever free model it routes to is
 * unknown until it answers, so promising vision would be a lie half the time.
 */
export function canSeeImages(models: AiModel[], value: string): boolean {
  const sep = value.indexOf(':');
  if ((sep > 0 ? value.slice(sep + 1) : value) === AUTO_ID) return false;
  return models.find((m) => `${m.provider}:${m.id}` === value)?.vision === true;
}

/**
 * Images out of a paste. `files` is what every current browser fills for a
 * screenshot paste; `items` is the fallback. A plain-text paste yields [] and
 * the caller leaves the event alone.
 */
export function imagesFromClipboard(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return [];
  const files = Array.from(dt.files ?? []);
  if (files.length) return files.filter((f) => f.type.startsWith('image/'));
  return Array.from(dt.items ?? [])
    .filter((i) => i.kind === 'file' && i.type.startsWith('image/'))
    .map((i) => i.getAsFile())
    .filter((f): f is File => !!f);
}

/**
 * Apply the composer's rules to a pick/paste. First problem wins the toast —
 * the person can see which chips landed. Only PICTURES need a vision model:
 * text, PDF, audio and video ride along with any model (AB#949).
 */
export function acceptAttachments(
  current: File[],
  picked: File[],
  canSee = true,
): { files: File[]; error?: string } {
  let error: string | undefined;
  const files = [...current];
  for (const file of picked) {
    const image = isImage(file);
    if (!image && !FILE_MIMES.includes(attachmentMime(file)))
      error ??= `${file.name} isn't a type Baxter can read.`;
    else if (image && !canSee) error ??= NO_VISION_NOTE;
    else if (file.size > MAX_BYTES) error ??= `${file.name} is over 10 MB.`;
    else if (files.length >= MAX_ATTACHMENTS)
      error ??= `Up to ${MAX_ATTACHMENTS} files per message.`;
    else files.push(file);
  }
  return { files, error };
}

/** The transcript should show what rode along with the message (any file type). */
export function withAttachmentNote(text: string, files: { name: string }[]): string {
  if (!files.length) return text;
  return `${text}\n(attached: ${files.map((f) => f.name).join(', ')})`;
}

/** Icon for a non-picture attachment, by mime family. */
export function FileIcon({ mime, className }: { mime: string; className?: string }) {
  const Icon = mime.startsWith('audio/') ? FileAudio : mime.startsWith('video/') ? FileVideo : FileText;
  return <Icon aria-hidden className={className} />;
}

/** A non-picture attachment: icon + name + size (composer queue and sent bubble). */
export function FileChip({ name, size, mime }: { name: string; size: number; mime: string }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5" data-testid="baxter-file-chip">
      <FileIcon mime={mime} className="size-4 shrink-0 text-muted-foreground" />
      <span className="max-w-32 truncate">{name}</span>
      <span className="shrink-0 text-muted-foreground">{formatSize(size)}</span>
    </span>
  );
}

/** The pending-attachment chips above the composer (same look as the New Site wizard's). */
export function Attachments({
  files,
  onRemove,
}: {
  files: File[];
  onRemove: (index: number) => void;
}) {
  // Only pictures get a thumbnail; the rest show a file chip.
  const previews = useMemo(
    () => files.map((f) => (isImage(f) ? URL.createObjectURL(f) : '')),
    [files],
  );
  useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews]);

  if (!files.length) return null;
  return (
    <div className="mb-1.5 flex flex-wrap gap-2" data-testid="baxter-attachments">
      {files.map((file, i) => (
        <span
          key={`${file.name}-${i}`}
          className="flex items-center gap-2 rounded-md border border-border bg-card py-1 pl-1 pr-2 text-xs"
        >
          {previews[i] ? (
            <>
              <img src={previews[i]} alt="" className="size-8 rounded object-cover" />
              <span className="max-w-32 truncate">{file.name}</span>
            </>
          ) : (
            <span className="py-1.5 pl-1">
              <FileChip name={file.name} size={file.size} mime={attachmentMime(file)} />
            </span>
          )}
          <button
            type="button"
            aria-label={`Remove ${file.name}`}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onRemove(i)}
          >
            <X className="size-3.5" />
          </button>
        </span>
      ))}
    </div>
  );
}
